import {
	AlertDialog,
	SearchInputForm,
	Toolbar,
	LassoContainer,
	lassoModeToScopeKind,
	Dialog,
	SearchPlaceholder
} from "client/Components";
import { contains } from "client/Data/searching";
import { AvailableAppModel, AvailableAppsViewModel } from "client/Api/Workspace/workspace";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useSelection,
	useStableImperativeCallback
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { getAppsAvailableToPin } from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import styles from "./AddAppsAsTabs.module.less";
import { AppListItem } from "./AppListItem";

export type AddAppsAsTabsProps = {
	onAdd(selectedApps: readonly AvailableAppModel[]): Promise<void> | void;
	onClose(): void;
	alreadyInstalledAppIdsThatShouldAlsoBeIncluded?: readonly number[];
};

export const AddAppsAsTabs = (props: AddAppsAsTabsProps): ReactElement => {
	const {
		onAdd,
		onClose: onCloseFromProps,
		alreadyInstalledAppIdsThatShouldAlsoBeIncluded
	} = props;

	const onClose: typeof onCloseFromProps = useStableImperativeCallback(onCloseFromProps);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [shouldShowInfo, setShouldShowInfo] = useState<boolean>(false);
	const [apps, setApps] = useState<readonly AvailableAppModel[]>([]);
	const [selectedItems, setSelectedItems] = useState<readonly AvailableAppModel[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const alertManager: AlertManager = useAlerts();

	const { currentItems } = useAppsDataSource(
		apps,
		{ searchTerm }
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		apps,
		"installedAppId",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const addHandler = useCallback(
		async (): Promise<void> => {
			if (apps.length === 0) {
				console.warn("List is not initialized yet");
				return;
			}

			setIsBusy(true);

			try {
				await onAdd(selectedItems);
				onClose();
			} catch(e) {
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			apps.length,
			onAdd,
			onClose,
			selectedItems
		]
	);

	useEffect(
		() => {
			async function fetchItems(signal: AbortSignal): Promise<void> {
				setIsBusy(true);
				try {
					const result: AvailableAppsViewModel = await getAppsAvailableToPin(
						alreadyInstalledAppIdsThatShouldAlsoBeIncluded,
						signal
					);
					const availableApps: readonly AvailableAppModel[] = result
						.availableApps
						.filter(a => !a.tileCanBeConfigured);

					setApps(availableApps);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			}

			const cancellation = new AbortController();
			fetchItems(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			alreadyInstalledAppIdsThatShouldAlsoBeIncluded,
			onClose
		]
	);

	const renderList = (): ReactElement | null => {

		if (apps.length === 0) {
			return <p>{localize("_ThereAreNoAppsToShow")}</p>;
		}

		return (
			<ul className={styles.list}>
				{currentItems.map(x => {
					return (
						<li key={x.installedAppId}>
							<AppListItem
								model={x}
								selectable={selectionFor(x)}
							/>
						</li>
					);
				})}
			</ul>
		);
	};

	return (
		<>
			<Dialog
				hasBusyIndicator={isBusy}
				trackingId="add-apps-as-tabs-slider"
				variant="left-slider"
				onClose={onClose}
			>
				<Dialog.Header
					title={localize("_AddWebContentAsSpace")}
					onClose={onClose}
					onShowInfo={() => setShouldShowInfo(true)}
					hasBorder
				/>
				<Dialog.Body>
					<div className={styles.search}>
						<SearchInputForm
							onChange={setSearchTerm}
							placeholder={
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Name")
									]}
								/>
							}
							trackingId="search-input"
							height="large"
							icon="filter"
						/>
					</div>
					<div className={styles.listContainer}>
						{currentItems.length > 1
							? (
								<LassoContainer
									onLassoStart={lm => multiSelection.startSelection(lassoModeToScopeKind[lm])}
									onLassoStop={() => multiSelection.finishSelection()}
								>
									{renderList()}
								</LassoContainer>
							)
							: renderList()
						}
					</div>
				</Dialog.Body>
				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_Add")}
							onClick={addHandler}
							isDisabled={selectedItems.length === 0}
							trackingId="add-button"
						/>

						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel-button"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{shouldShowInfo && (
				<AlertDialog
					title={localize("_AddWebContentAsSpace")}
					onClose={() => setShouldShowInfo(false)}
					showCloseButton
					trackingId="more-info-dialog"
				>
					{localize("_AddWebContentAsSpaceInfoText")}
				</AlertDialog>
			)}
		</>
	);
};
AddAppsAsTabs.displayName = "AddAppsAsTabs";

export function useAddAppsAsTabsSidebar(): [
	element: ReactElement | null,
	open: (props: Omit<AddAppsAsTabsProps, "onClose">) => void
] {
	const [props, setProps] = useState<AddAppsAsTabsProps | null>(null);

	const renderSidebar = (): ReactElement | null => {
		if (!props) {
			return null;
		}

		return (
			<AddAppsAsTabs {...props} />
		);
	};

	const open = useCallback(
		(props: Omit<AddAppsAsTabsProps, "onClose">): void => {
			setProps({
				...props,
				onClose() {
					setProps(null);
				},
			});
		},
		[]
	);

	return [
		renderSidebar(),
		open
	];
}

const useAppsDataSource = createLocalDataSource<AvailableAppModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) =>
				contains(item.name, searchTerm)
		}
	})
	.getHook();